import { useFetchTruckLocation } from "./useFetchTruckLocation.";
import { useFetchSimTracking } from "./useFetchSimTracking";

// Helpers to transform tracking locations and status updates
const formatPing = (loc) => ({
  status: loc.location || "Location",
  location: loc.location || "",
  timestamp: loc.time_recorded || "",
  coordinates: {
    lat: loc.latitude,
    lng: loc.longitude,
  },
  phoneNumber: loc.phone_number,
  kind: "ping",
});

const getStatusTimestamp = (statusTrack, value) =>
  statusTrack?.find((c) => c?.value === value)?.timestamp || "";

const formatStatusUpdates = (origin, destination, pings, statusTrack) => [
  {
    status: destination.label || destination.location || "Destination",
    location: destination.location || "",
    timestamp: getStatusTimestamp(statusTrack, "reached"),
    kind: "destination",
  },
  ...pings,
  {
    status: origin.label || origin.location || "Origin",
    location: origin.location || "",
    timestamp: getStatusTimestamp(statusTrack, "dispatched"),
    kind: "origin",
  },
];

// Handles fetching, extracting, and transforming all data needed by TrackDetails.js.
export const useTrackDetailsData = (indent_id, indent_id_sub) => {
  const {
    fetchTruckLocation,
    trackData,
    loadingTruckLocation,
    loadingIndentDetails,
    err,
    indentDetails,
    fetchIndentDetails,
    globalErr,
  } = useFetchTruckLocation(indent_id, indent_id_sub);

  const { fetchSimTracking, simTrackData, loadingSimTracking, simErr } =
    useFetchSimTracking(indent_id);

  // Safely extract nested indent details
  const indentInfo = indentDetails?.indent_details?.indent_info || {};
  const vehicleTracking = indentDetails?.indent_details?.vehicle_tracking || {};
  const truckDetails = vehicleTracking?.truck_details || {};

  const origin = indentInfo.origin || {};
  const destination = indentInfo.destination || {};
  const dateOfPlacement = indentInfo.placement_date || "-";
  const statusTrack = vehicleTracking.status_track || [];

  const route = `${origin.location?.label || origin.location?.suggestion || "Origin"} → ${destination.location?.label || destination.location?.suggestion || "Destination"
    }`;

  const carrier = {
    trackingLink: truckDetails.gps_link || "",
    name: vehicleTracking.transporter_name || "—",
    phone: truckDetails.driver_phone_number || "",
    vehicleNumber: truckDetails.vehicle_no || "",
    driverName: truckDetails.driver_name || "",
    truckType: indentInfo.truck_types?.[0]?.value || "",
  };
  const indentConsignment = indentDetails?.[0]?.lr_details?.consignment_details || {};



  // Extract Current Status
  const currentStatusObj = statusTrack?.slice().reverse().find(s => s.timestamp) || statusTrack?.[statusTrack.length - 1];
  const currentStatus = currentStatusObj?.value?.replace(/_/g, " ") || "--";

  // Process Toll/Fastag data
  const truckLocs = Array.isArray(trackData?.locations) ? trackData.locations : [];
  const updates = truckLocs.map(formatPing);
  const statusUpdates = formatStatusUpdates(origin, destination, updates, statusTrack);

  // Process SIM tracking data
  const simLocs = Array.isArray(simTrackData?.locations) ? simTrackData.locations : [];
  const simUpdates = simLocs.map(formatPing);
  const simStatusUpdates = formatStatusUpdates(origin, destination, simUpdates, statusTrack);

  const commonData = {
    route,
    origin,
    destination,
    carrier,
    dateOfPlacement,
    indentConsignment,
    indentDetails,
    status: statusTrack,
    currentStatus,
  };

  const data = {
    ...commonData,
    statusUpdates,
    truckLocs,
    trackData,
    actualRoute: trackData?.actual_route || [],
    feasibleRoute: trackData?.feasible_route || [],
  };

  const simData = {
    ...commonData,
    statusUpdates: simStatusUpdates,
    truckLocs: simLocs,
    actualRoute: simTrackData?.actual_route || [],
  };

  return {
    loadingIndentDetails,
    loadingTruckLocation,
    loadingSimTracking,
    err,
    simErr: simErr === "No SIM tracking details found" ? null : simErr,
    fetchTruckLocation,
    fetchSimTracking,
    fetchIndentDetails,
    data,
    simData,
    globalErr,
  };
};

